import { createFileRoute, Link, useLocation } from '@tanstack/react-router'
import {
  ArrowRight,
  Boxes,
  Cable,
  CheckCircle2,
  CloudCog,
  Cpu,
  Gauge,
  KeyRound,
  Network,
  RadioTower,
  Router,
  ShieldCheck,
  Signal,
  Wifi,
} from 'lucide-react'
import { motion } from 'motion/react'
import { designSystem } from '@/lib/design-system'
import { cn } from '@/lib/utils'
import { LandingPageLayout } from '@/components/landing/LandingPageLayout'
import { SectionScroller } from '@/components/landing/SectionScroller'
import { getLocaleFromPath, withLocalePath } from '@/lib/i18n-utils'
import { GridSpotlight } from '@/components/ui/grid-spotlight'
import { ServiceHeroVisual } from '@/components/services/ServiceHeroVisual'

export const Route = createFileRoute('/{-$locale}/services/connect')({
  head: () => ({
    meta: [
      { title: 'Data Ingestion — Ponte Solutions' },
      {
        name: 'description',
        content:
          'Data ingestion by Ponte Solutions. Edge integration, secure provisioning, protocol translation, and cloud ingest for connected hardware products.',
      },
      { property: 'og:title', content: 'Data Ingestion — Ponte Solutions' },
      {
        property: 'og:description',
        content:
          'Data ingestion by Ponte Solutions. Edge integration, secure provisioning, protocol translation, and cloud ingest for connected hardware products.',
      },
    ],
  }),
  component: ConnectPage,
})

const fieldSignals = [
  {
    icon: Cable,
    title: 'Field devices speak different languages.',
    text: 'MQTT, Modbus, OPC-UA, serial links, custom firmware, PLCs, gateways, and machines that were never designed for cloud telemetry.',
  },
  {
    icon: Signal,
    title: 'Networks are imperfect.',
    text: 'Network links drop, latency changes, cellular costs matter, and a production system cannot lose data every time a site has weak coverage.',
  },
  {
    icon: Gauge,
    title: 'Raw telemetry gets expensive fast.',
    text: 'A connected product program needs filtering, batching, routing, and cost controls before high-volume data reaches long-term storage.',
  },
]

const architectureLayers = [
  {
    icon: Cpu,
    title: 'Device and Edge',
    text: 'We connect existing product electronics, PLCs, sensors, or local controllers through firmware changes, embedded services, or edge gateways.',
  },
  {
    icon: ShieldCheck,
    title: 'Secure Provisioning',
    text: 'Each asset gets a clear identity model: certificates, token rotation, enrollment workflows, and deployment rules your IT team can approve.',
  },
  {
    icon: Router,
    title: 'Cloud Ingest',
    text: 'Telemetry is normalized and routed into the cloud environment that fits your product strategy, whether that is Azure, AWS, Google Cloud, or a mixed architecture.',
  },
  {
    icon: CloudCog,
    title: 'Business-Ready Data',
    text: 'Events arrive clean enough for monitoring, service workflows, customer portals, BI, and predictive maintenance instead of remaining a disconnected technical feed.',
  },
]

const deliverySteps = [
  {
    eyebrow: '01',
    title: 'Ingestion Audit',
    text: 'We inspect devices, protocols, network realities, telemetry volume, security constraints, and the business decisions the data should support.',
  },
  {
    eyebrow: '02',
    title: 'Pilot Data Path',
    text: 'We build a working route from representative hardware to the cloud with buffering, identity, schema, routing, and operating visibility included.',
  },
  {
    eyebrow: '03',
    title: 'Fleet Rollout',
    text: 'We harden deployment, provisioning, monitoring, cost controls, and runbooks so your team can operate the connected product layer confidently.',
  },
]

const outcomes = [
  'Reliable device-to-cloud data flow',
  'Lower telemetry and storage waste',
  'Fleet provisioning model',
  'A foundation for Data Processing & Analytics and Action Intelligence',
]

function ConnectPage() {
  return (
    <LandingPageLayout>
      <div className="ponte-service-page">
        <SectionScroller labels={['Ingest', 'Reality', 'Architecture', 'Delivery', 'Start']}>
          <ConnectHeroSection />
          <FieldRealitySection />
          <ArchitectureSection />
          <DeliverySection />
          <ConnectCtaSection />
        </SectionScroller>
      </div>
    </LandingPageLayout>
  )
}

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
}

function onSectionMouseMove(e: React.MouseEvent<HTMLElement>) {
  const rect = e.currentTarget.getBoundingClientRect()
  e.currentTarget.style.setProperty('--spot-x', `${e.clientX - rect.left}px`)
  e.currentTarget.style.setProperty('--spot-y', `${e.clientY - rect.top}px`)
}

function onCardMouseMove(e: React.MouseEvent<HTMLElement>) {
  const rect = e.currentTarget.getBoundingClientRect()
  e.currentTarget.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`)
  e.currentTarget.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`)
}

function CardSpotlight() {
  return (
    <>
      <div className="pointer-events-none absolute inset-0 z-0 rounded-xl opacity-0 backdrop-blur-[1px] transition-opacity duration-200 group-hover:opacity-100" />
      <div className="pointer-events-none absolute inset-0 z-0 rounded-xl bg-accent/[0.08] opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
      <div
        className="pointer-events-none absolute inset-0 z-0 rounded-xl opacity-0 transition-opacity duration-200 group-hover:opacity-100"
        style={{
          background:
            'radial-gradient(240px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), oklch(68% .095 52 / 0.2), transparent 70%)',
        }}
      />
    </>
  )
}

/* ─── Hero ─── */
function ConnectHeroSection() {
  const location = useLocation()
  const locale = getLocaleFromPath(location.pathname)

  return (
    <section
      className="ponte-service-section ponte-service-section--hero relative isolate flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-10 text-white sm:px-6 lg:px-8"
      style={{ '--spot-x': '50%', '--spot-y': '50%' } as React.CSSProperties}
      onMouseMove={onSectionMouseMove}
    >
      <GridSpotlight />
      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-10 lg:grid-cols-[0.9fr_1.1fr]">
        <motion.div
          className="max-w-3xl py-10"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <motion.div
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/8 px-3 py-1.5 text-xs font-medium text-white/75"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.1 }}
          >
            <Wifi className="h-3.5 w-3.5 text-accent" />
            Data Ingestion
          </motion.div>

          <motion.h1
            className={cn(
              designSystem.typography.heading.h1,
              'max-w-4xl text-balance text-[clamp(2.8rem,7vw,5.5rem)] font-semibold leading-[0.92] tracking-normal text-white',
            )}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.2, ease: 'easeOut' }}
          >
            Get your product{' '}
            <span className="text-accent">data moving.</span>
          </motion.h1>

          <motion.p
            className="mt-7 max-w-2xl text-pretty text-lg leading-8 text-white/72 md:text-xl"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            Ponte builds the data ingestion layer around hardware your company already makes:
            edge integration, secure provisioning, protocol translation, and cloud ingest that
            survives real field conditions.
          </motion.p>

          <motion.div
            className="mt-9 flex flex-col gap-3 sm:flex-row"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.4 }}
          >
            <Link
              to={withLocalePath('/contact', locale)}
              className="landing-cta-primary inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold"
            >
              Discuss Data Ingestion
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to={withLocalePath('/services/data-processing-analytics', locale)}
              className="landing-cta-secondary inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold"
            >
              Next: Data Processing & Analytics
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </motion.div>
        <ServiceHeroVisual service="connect" />
      </div>
    </section>
  )
}

/* ─── Field Reality ─── */
function FieldRealitySection() {
  return (
    <section className="ponte-service-section ponte-service-section--deep relative flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-14 sm:px-6 lg:px-8">
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_20%_60%,rgba(214,151,101,.1),transparent_40%)]"
      />

      <motion.div
        className="relative z-10 mx-auto grid w-full max-w-7xl gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-center"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-80px' }}
        variants={containerVariants}
      >
        <motion.div variants={itemVariants}>
          <span className={cn(designSystem.typography.display.eyebrow, 'mb-3 block dark:font-mono dark:text-primary/70')}>
            Field Reality
          </span>
          <h2
            className={cn(
              designSystem.typography.heading.h1,
              'max-w-xl text-balance text-3xl font-semibold tracking-normal text-white sm:text-4xl lg:text-5xl',
            )}
          >
            The hard part is not the cloud. It is{' '}
            <span className="text-accent">everything before the cloud.</span>
          </h2>
          <p className="mt-5 max-w-xl text-base leading-8 text-white/60">
            Connected product programs usually fail in the messy middle between hardware, networks,
            security, and operations. Data ingestion is the layer that makes the rest of the service
            platform possible.
          </p>
        </motion.div>

        <motion.div className="grid gap-4" variants={containerVariants}>
          {fieldSignals.map((item) => (
            <motion.div
              key={item.title}
              variants={itemVariants}
              className="group relative overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.03] p-5 backdrop-blur-sm transition-all duration-300 hover:border-accent/40"
              style={{ '--mouse-x': '50%', '--mouse-y': '50%' } as React.CSSProperties}
              onMouseMove={onCardMouseMove}
            >
              <CardSpotlight />
              <div className="relative z-10 grid gap-4 sm:grid-cols-[3rem_1fr]">
                <div className="flex h-12 w-12 items-center justify-center rounded-md bg-accent/10 text-accent transition-colors duration-300 group-hover:bg-accent group-hover:text-accent-foreground">
                  <item.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-white/55">{item.text}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  )
}

/* ─── Architecture ─── */
function ArchitectureSection() {
  return (
    <section
      className="ponte-service-section ponte-service-section--mesh relative flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-14 text-white sm:px-6 lg:px-8"
      style={{ '--spot-x': '50%', '--spot-y': '50%' } as React.CSSProperties}
      onMouseMove={onSectionMouseMove}
    >
      <GridSpotlight />
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_50%_38%,rgba(214,151,101,.16),transparent_36%)]"
      />

      <motion.div
        className="relative z-10 mx-auto w-full max-w-7xl"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-80px' }}
        variants={containerVariants}
      >
        <motion.div className="mx-auto max-w-3xl text-center" variants={itemVariants}>
          <span className={cn(designSystem.typography.display.eyebrow, 'mb-3 block dark:font-mono dark:text-primary/70')}>
            Data Ingestion Architecture
          </span>
          <h2
            className={cn(
              designSystem.typography.heading.h1,
              'text-balance text-3xl font-semibold tracking-normal text-white sm:text-4xl lg:text-5xl',
            )}
          >
            From field signals to{' '}
            <span className="text-accent">usable product data.</span>
          </h2>
          <p className="mt-5 text-base leading-8 text-white/55">
            We design the path as one system, not as disconnected scripts: device interface,
            identity, buffering, cloud ingest, routing, and the data contract used by every
            downstream product or service workflow.
          </p>
        </motion.div>

        <div className="mt-12 grid gap-4 lg:grid-cols-4">
          {architectureLayers.map((layer, index) => (
            <motion.div
              key={layer.title}
              variants={itemVariants}
              className="group relative overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.025] p-5 backdrop-blur-sm transition-all duration-300 hover:border-accent/30"
              style={{ '--mouse-x': '50%', '--mouse-y': '50%' } as React.CSSProperties}
              onMouseMove={onCardMouseMove}
            >
              {index < architectureLayers.length - 1 && (
                <div className="pointer-events-none absolute -right-2 top-[2.75rem] z-20 hidden h-[2px] w-4 bg-gradient-to-r from-accent/40 to-transparent lg:block" />
              )}
              <CardSpotlight />
              <div className="relative z-10">
                <div className="mb-8 flex items-center justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-md bg-accent text-accent-foreground transition-transform duration-300 group-hover:scale-105">
                    <layer.icon className="h-5 w-5" />
                  </div>
                  <span className="font-mono text-xs text-white/30">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-white transition-colors duration-300 group-hover:text-accent">
                  {layer.title}
                </h3>
                <p className="mt-3 text-sm leading-7 text-white/50">{layer.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  )
}

/* ─── Delivery ─── */
function DeliverySection() {
  return (
    <section className="ponte-service-section ponte-service-section--deep relative flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-14 text-white sm:px-6 lg:px-8">
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,rgba(214,151,101,.1),transparent_40%)]"
      />

      <motion.div
        className="relative z-10 mx-auto w-full max-w-7xl"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-80px' }}
        variants={containerVariants}
      >
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <motion.div variants={itemVariants}>
            <span className={cn(designSystem.typography.display.eyebrow, 'mb-3 block dark:font-mono dark:text-primary/70')}>
              Delivery Model
            </span>
            <h2
              className={cn(
                designSystem.typography.heading.h1,
                'max-w-xl text-balance text-3xl font-semibold tracking-normal text-white sm:text-4xl lg:text-5xl',
              )}
            >
              Start with one{' '}
              <span className="text-accent">real path</span> from device to decision.
            </h2>
            <p className="mt-5 max-w-xl text-base leading-8 text-white/55">
              We keep the first engagement concrete. One representative device group, one
              production-shaped data path, one operating model your team can evaluate before the
              rollout expands.
            </p>
          </motion.div>

          <motion.div className="grid gap-4" variants={containerVariants}>
            {deliverySteps.map((step) => (
              <motion.div
                key={step.title}
                variants={itemVariants}
                className="group relative overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.025] p-5 backdrop-blur-sm transition-all duration-300 hover:border-accent/30"
                style={{ '--mouse-x': '50%', '--mouse-y': '50%' } as React.CSSProperties}
                onMouseMove={onCardMouseMove}
              >
                <CardSpotlight />
                <div className="relative z-10 grid gap-4 sm:grid-cols-[4.5rem_1fr]">
                  <div className="flex h-14 w-14 items-center justify-center rounded-md border border-accent/30 bg-accent/10 font-mono text-sm font-semibold text-accent transition-colors duration-300 group-hover:border-accent/50 group-hover:bg-accent/15">
                    {step.eyebrow}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white transition-colors duration-300 group-hover:text-accent">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-sm leading-7 text-white/50">{step.text}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.div>
    </section>
  )
}

/* ─── CTA ─── */
function ConnectCtaSection() {
  const location = useLocation()
  const locale = getLocaleFromPath(location.pathname)

  return (
    <section
      className="ponte-service-section ponte-service-section--cta relative flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-14 text-white sm:px-6 lg:px-8"
      style={{ '--spot-x': '50%', '--spot-y': '50%' } as React.CSSProperties}
      onMouseMove={onSectionMouseMove}
    >
      <GridSpotlight gridSize={88} baseOpacity={0.12} highlightOpacity={0.22} />
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(214,151,101,.14),transparent_45%)]"
      />

      <motion.div
        className="relative z-10 mx-auto grid w-full max-w-7xl gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-80px' }}
        variants={containerVariants}
      >
        <motion.div variants={itemVariants}>
          <span className={cn(designSystem.typography.display.eyebrow, 'mb-3 block dark:font-mono dark:text-primary/70')}>
            What Data Ingestion Unlocks
          </span>
          <h2
            className={cn(
              designSystem.typography.heading.h1,
              'max-w-2xl text-balance text-3xl font-semibold tracking-normal text-white sm:text-4xl lg:text-5xl',
            )}
          >
            Once data flows reliably,{' '}
            <span className="text-accent">every other service layer</span> becomes easier.
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-8 text-white/55">
            Data ingestion gives your product team and service organization a stable foundation for fleet
            visibility, customer portals, business-system integration, and predictive maintenance.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={withLocalePath('/contact', locale)}
              className="landing-cta-primary inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold"
            >
              Start a Data Ingestion Project
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={withLocalePath('/services/data-processing-analytics', locale)}
              className="landing-cta-secondary inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold"
            >
              Explore Data Processing & Analytics
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="group relative overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.025] p-6 backdrop-blur-sm transition-all duration-300 hover:border-accent/25"
          style={{ '--mouse-x': '50%', '--mouse-y': '50%' } as React.CSSProperties}
          onMouseMove={onCardMouseMove}
        >
          <CardSpotlight />
          <div className="relative z-10">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-md bg-accent text-accent-foreground">
                <Network className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Data ingestion outcomes</h3>
                <p className="text-sm text-white/50">A practical base for the connected product stack.</p>
              </div>
            </div>

            <div className="grid gap-3">
              {outcomes.map((outcome) => (
                <div
                  key={outcome}
                  className="flex items-start gap-3 rounded-lg bg-white/[0.04] p-3 transition-colors duration-200 hover:bg-white/[0.07]"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  <span className="text-sm font-medium text-white/85">{outcome}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-3 gap-2 text-center">
              {[
                { icon: RadioTower, label: 'Ingest' },
                { icon: KeyRound, label: 'Identity' },
                { icon: Boxes, label: 'Routing' },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-lg border border-white/[0.06] bg-white/[0.03] p-3 transition-all duration-200 hover:border-accent/20 hover:bg-white/[0.05]"
                >
                  <item.icon className="mx-auto h-4 w-4 text-accent" />
                  <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/50">
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  )
}
